import { Link } from "react-router-dom";
import { formatMoney } from "../../api/money";
import type { QuotationRow } from "../../api/quotations";
import { StatusChip } from "../../components/ui";
import { STEPS, SUPPLIER_STEPS, factsOfRow, jobRef, stepsOf } from "./pipeline";
import { quotationLabel, quotationTone } from "./tones";

interface Props {
  row: QuotationRow;
  selected: boolean;
  supplierView: boolean;
}

/** One job in the rail: its reference, where the quotation stands, the money,
 * the site and supplier, and a strip of six segments -- one per document --
 * so the list reads as progress without opening a job.
 *
 * A link, because choosing a job is going to its address: Enter and a click
 * both select it, a middle-click opens it beside this one, and the back button
 * returns to the job before (Fix #17: the old `<tr onClick>` could not be
 * reached from the keyboard at all). */
export default function JobRow({ row, selected, supplierView }: Props) {
  const ref = jobRef(row.supplier, row.number);
  const steps = stepsOf(factsOfRow(row), supplierView ? SUPPLIER_STEPS : STEPS);
  const site = row.site?.name ?? "site not linked";
  return (
    <Link
      to={`/commercial/${row.id}`}
      className={selected ? "cm-row selected" : "cm-row"}
      aria-current={selected ? "page" : undefined}
      data-testid={`job-row-${row.id}`}
    >
      <span className="cm-row-top">
        <span className="cm-row-ref mono">{ref}</span>
        <StatusChip tone={quotationTone(row.status)}>{quotationLabel(row.status)}</StatusChip>
        <span className="cm-row-money">{formatMoney(row.total_excl_vat)}</span>
      </span>
      <span className="cm-row-bottom">
        <span className="cm-row-where">
          {site}
          {supplierView ? "" : ` · ${row.supplier?.name ?? "unknown supplier"}`}
        </span>
        <span className="cm-segs" aria-hidden="true">
          {steps.map((step) => (
            <span key={step.key} className={`cm-seg ${step.state}`} title={`${step.label}: ${step.sub}`} />
          ))}
        </span>
      </span>
    </Link>
  );
}
